import {
  MsgRedactedBatchCalls,
  MsgRedactedCall,
  MsgRedactedThorchainDeposit,
  type Msg,
  type RedactedContext,
} from "rujira.js";

/**
 * Invoke private message translators through their owning message instance.
 * Several translators call another method through `this`, so extracting the
 * selected function before invoking it breaks every such conversion.
 */
export const translatePrivateMessage = (
  message: Msg,
  v2Enabled: boolean,
  context: RedactedContext
): Msg | undefined =>
  v2Enabled ? message.toPrivateV2?.(context) : message.toPrivate?.(context);

const BASE64 = /^[A-Za-z0-9+/]+={0,2}$/;

const decodeBase64Text = (value: string): string | undefined => {
  if (value.length < 16 || value.length % 4 !== 0 || !BASE64.test(value))
    return undefined;
  try {
    const bytes = Uint8Array.from(atob(value), (char) => char.charCodeAt(0));
    return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    return undefined;
  }
};

const parseJson = (text: string): unknown => {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
};

/** Every text in a call, including base64 contract messages and JSON. */
const texts = (value: unknown, out: string[], depth = 0): string[] => {
  if (depth > 8) return out;
  if (typeof value === "string") {
    out.push(value);
    const decoded = decodeBase64Text(value);
    if (decoded !== undefined) texts(decoded, out, depth + 1);
    if (/^\s*[[{]/.test(value)) {
      const parsed = parseJson(value);
      if (parsed !== undefined) texts(parsed, out, depth + 1);
    }
  } else if (Array.isArray(value)) {
    value.forEach((item) => texts(item, out, depth + 1));
  } else if (value && typeof value === "object") {
    Object.values(value).forEach((item) => texts(item, out, depth + 1));
  }
  return out;
};

/**
 * A private action runs from the Spending account. A connected wallet's
 * address anywhere in what it executes (a recipient, a memo destination, a
 * contract message) would link the two accounts on-chain, so such an action
 * is never sent. The designed way back to a connected wallet is the Private
 * Reserve withdrawal, which is not a call from the Spending account.
 */
export const privateActionNamesConnectedWallet = (
  message: Msg | null | undefined,
  connectedAddresses: readonly string[]
): boolean => {
  const payload =
    message instanceof MsgRedactedBatchCalls
      ? message.calls
      : message instanceof MsgRedactedCall
        ? message.call
        : message instanceof MsgRedactedThorchainDeposit
          ? message.memo
          : undefined;
  if (payload === undefined) return false;
  const haystack = texts(payload, []).join("\n").toLowerCase();
  return connectedAddresses.some(
    (address) =>
      address.length >= 20 && haystack.includes(address.toLowerCase())
  );
};
